import {
  BarChart3,
  Bug,
  CalendarDays,
  CircleDollarSign,
  CreditCard,
  ImagePlus,
  Images,
  Layers,
  LayoutTemplate,
  ListTree,
  Megaphone,
  SlidersHorizontal,
  UserCheck,
  Users,
  type LucideIcon,
} from "lucide-react";
import {
  RUNTIME_FLAG_GROUPS,
  RUNTIME_FLAG_ROOT_PATH,
  getRuntimeFlagGroupPath,
} from "@/lib/runtime-flag-groups";

type AdminNavChildItem = {
  label: string;
  href: string;
};

type AdminNavItem = {
  label: string;
  href: string;
  icon: LucideIcon;
  children?: readonly AdminNavChildItem[];
};

type AdminNavGroup = {
  label: string;
  items: readonly AdminNavItem[];
};

const matchesNavHref = (pathname: string, href: string) =>
  pathname === href || pathname.startsWith(`${href}/`);

const matchesChildNavHref = (pathname: string, parentHref: string, childHref: string) =>
  pathname === childHref || (childHref !== parentHref && pathname.startsWith(`${childHref}/`));

export const getAdminNavItemActiveState = (
  pathname: string,
  item: Pick<AdminNavItem, "href" | "children">,
) => {
  const activeChildHref =
    item.children?.find((child) => matchesChildNavHref(pathname, item.href, child.href))?.href ??
    null;

  return {
    isActive: activeChildHref === null && matchesNavHref(pathname, item.href),
    activeChildHref,
  };
};

export const ADMIN_NAV_GROUPS: readonly AdminNavGroup[] = [
  {
    label: "Workspace",
    items: [
      {
        label: "Product Stats",
        href: "/stats",
        icon: BarChart3,
      },
      {
        label: "Brands",
        href: "/brands",
        icon: Layers,
      },
      {
        label: "Users",
        href: "/users",
        icon: Users,
      },
      {
        label: "Workspaces",
        href: "/workspaces",
        icon: CreditCard,
      },
      {
        label: "Payments",
        href: "/payments",
        icon: CreditCard,
      },
      {
        label: "Billing Plans",
        href: "/billing-plans",
        icon: CreditCard,
      },
      {
        label: "Run Packs",
        href: "/credit-packs",
        icon: CreditCard,
      },
      {
        label: "Release Access",
        href: "/release-access",
        icon: UserCheck,
      },
    ],
  },
  {
    label: "Publishing & Ops",
    items: [
      {
        label: "Instagram Publishing",
        href: "/instagram-debug",
        icon: Bug,
      },
      {
        label: "Worker Runs",
        href: "/runs",
        icon: ListTree,
      },
      {
        label: "Image Regenerations",
        href: "/image-regenerations",
        icon: ImagePlus,
      },
      {
        label: "Calendar Ops",
        href: "/calendar",
        icon: CalendarDays,
      },
      {
        label: "Runtime Flags",
        href: RUNTIME_FLAG_ROOT_PATH,
        icon: SlidersHorizontal,
        children: [
          { label: "All", href: RUNTIME_FLAG_ROOT_PATH },
          ...RUNTIME_FLAG_GROUPS.map((group) => ({
            label: group.title,
            href: getRuntimeFlagGroupPath(group),
          })),
        ],
      },
      {
        label: "Model Pricing",
        href: "/model-pricing",
        icon: CircleDollarSign,
      },
    ],
  },
  {
    label: "Expert Tools",
    items: [
      {
        label: "Social Planner",
        href: "/social-post-planner",
        icon: Megaphone,
      },
      {
        label: "Carousel Formats",
        href: "/carousel-formats",
        icon: Images,
      },
      {
        label: "Layout Templates",
        href: "/layout-templates",
        icon: LayoutTemplate,
      },
    ],
  },
];

export const getAdminNavItemLabels = (): string[] =>
  ADMIN_NAV_GROUPS.flatMap((group) =>
    group.items.flatMap((item) => [
      item.label,
      ...(item.children?.map((child) => child.label) ?? []),
    ]),
  );
